/**
 * subdomain.js — Tenant detection via subdomain
 *
 * When users visit harvard.vitrinedev.site, this utility detects the
 * subdomain and returns the tenant slug so the app can:
 *   - Show the institution's branding on the login page
 *   - Pre-fill the universityId on registration
 *   - Restrict login to users belonging to that university
 *
 * Base domains we recognize (update to match REACT_APP_BASE_DOMAIN):
 *   vitrinedev.site  →  sub.vitrinedev.site
 *   localhost        →  sub.localhost (for local dev)
 */

const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || "vitrinedev.site";

/**
 * Returns the subdomain slug if the current hostname is a tenant subdomain,
 * or null if we're on the root domain or localhost without a subdomain.
 *
 * Examples:
 *   harvard.vitrinedev.site   → "harvard"
 *   mit.vitrinedev.site       → "mit"
 *   vitrinedev.site           → null
 *   app.vitrinedev.site       → null  (reserved system subdomains)
 *   localhost                 → null
 *   harvard.localhost         → "harvard"  (dev mode)
 */

const SYSTEM_SUBDOMAINS = new Set(["www", "app", "api", "admin", "mail", "staging", "dev"]);

export const detectSubdomain = () => {
  const hostname = window.location.hostname;

  // Local dev shortcut: sub.localhost
  if (hostname.endsWith(".localhost")) {
    const sub = hostname.replace(".localhost", "");
    return SYSTEM_SUBDOMAINS.has(sub) ? null : sub;
  }

  // Production subdomain: sub.vitrinedev.site
  const suffix = `.${BASE_DOMAIN}`;
  if (hostname.endsWith(suffix)) {
    const sub = hostname.slice(0, hostname.length - suffix.length);
    // Reject empty or system subdomains
    if (!sub || SYSTEM_SUBDOMAINS.has(sub)) return null;
    return sub;
  }

  return null;
};

/**
 * Build the full portal URL for a given subdomain.
 *   buildPortalUrl("harvard") → "https://harvard.vitrinedev.site"
 */
export const buildPortalUrl = (subdomain) =>
  `https://${subdomain}.${BASE_DOMAIN}`;

/**
 * React hook — returns { subdomain, isSubdomainTenant }
 */
export const useSubdomain = () => {
  const subdomain = detectSubdomain();
  return {
    subdomain,
    isSubdomainTenant: subdomain !== null,
  };
};
